/**
 * Стиль админки на основе мобильной карты с адресными подписями. Палитра и правки слоёв живут в
 * packages/player/src/play/map-style.ts — при смене вида приложения
 * сохранять адресные подписи админки.
 *
 * База — Stadia Alidade Smooth Dark: тот же набор слоёв, палитру
 * перекрашиваем до MapView. Светлая бумага в одном сером: вода и парки
 * на шаг темнее земли, улицы белые. Цвет остаётся у фотографий.
 */

const STADIA_API_KEY = '0edec9b6-fb20-4e18-bfe2-fe2c35a01865';

const STADIA_DARK_STYLE_URL = `https://tiles.stadiamaps.com/styles/alidade_smooth_dark.json?api_key=${STADIA_API_KEY}`;

/**
 * Земля — якорь. Вода, парки и дороги остаются в том же сером,
 * меняется только светлота, без отдельного синего и зелёного.
 */
const PALETTE = {
    bg: '#E8E9ED',
    park: '#DDE0E6',
    wood: '#D5D9E1',
    water: '#C9CED6',
    waterway: '#B7BEC8',
    road: '#FFFFFF',
    roadMajor: '#FFFFFF',
    roadCasing: '#E8E9ED',
    boundary: 'rgba(70, 78, 90, 0.22)',
    label: 'rgba(58, 64, 74, 0.88)',
    labelMajor: 'rgba(28, 32, 38, 0.94)',
    labelHalo: 'rgba(255, 255, 255, 0.94)',
    waterLabel: 'rgba(72, 80, 94, 0.72)',
    shieldText: 'rgba(58, 64, 74, 0.45)',
} as const;

/** Подписи поверх карты: тёмные чернила и светлое гало. */
export const MAP_INK = '#1A1C1F';
export const MAP_LABEL_HALO = 'rgba(255, 255, 255, 0.95)';

/** Линия маршрута — рисунок по белым улицам. Синий остаётся у геолокации. */
export const ROUTE_LINE_COLOR = MAP_INK;

/**
 * Районы на общей карте — цветная дымка под улицами.
 * Во вкладке «Районы» заливка сплошная.
 */
export const DISTRICT_WASH_OPACITY = 0.5;

/**
 * Заливка района встаёт сразу под дороги. Вода, каналы и подписи
 * остаются под цветом, поверх видны только линии улиц.
 */
export const DISTRICT_FILL_BELOW_LAYER_ID = 'tunnel_motorway_casing';

const DISTRICT_LABEL_SOURCE_LAYERS = [
    'transportation_name',
    'place',
    'water_name',
    'aerodrome_label',
] as const;

type MapWithSourceVisibility = {
    setSourceVisibility: (
        visible: boolean,
        sourceId: string,
        sourceLayerId?: string | null,
    ) => void;
};

/** Подписи карты. На слое районов их прячем: поверх цвета остаются улицы. */
export const setDistrictBasemapLabels = (map: MapWithSourceVisibility | null, visible: boolean) => {
    if (!map) return;

    for (const sourceLayerId of DISTRICT_LABEL_SOURCE_LAYERS) {
        map.setSourceVisibility(visible, 'openmaptiles', sourceLayerId);
    }
};

const thin = (stops: number[]): unknown[] => ['interpolate', ['linear'], ['zoom'], ...stops];

type PaintProperty =
    | 'fill-color'
    | 'fill-opacity'
    | 'line-color'
    | 'line-opacity'
    | 'line-width'
    | 'background-color'
    | 'text-color'
    | 'text-halo-color'
    | 'text-halo-width'
    | 'text-halo-blur'
    | 'text-opacity'
    | 'icon-opacity';

type PaintValue = string | number | unknown[];

type PaintOverride = {
    layerPattern: RegExp;
    property: PaintProperty;
    value: PaintValue;
};

const PAINT_OVERRIDES: PaintOverride[] = [
    { layerPattern: /^building$/, property: 'fill-color', value: '#CED2D9' },
    {
        layerPattern: /^background$/,
        property: 'background-color',
        value: PALETTE.bg,
    },

    { layerPattern: /^park_fill$/, property: 'fill-color', value: PALETTE.park },
    { layerPattern: /^park_fill$/, property: 'fill-opacity', value: 1 },
    {
        layerPattern: /^landcover_park$/,
        property: 'fill-color',
        value: PALETTE.park,
    },
    { layerPattern: /^landcover_park$/, property: 'fill-opacity', value: 1 },
    {
        layerPattern: /^landcover_wood$/,
        property: 'fill-color',
        value: PALETTE.wood,
    },
    { layerPattern: /^landcover_wood$/, property: 'fill-opacity', value: 0.8 },
    {
        layerPattern: /^landcover_(ice_shelf|glacier)$/,
        property: 'fill-color',
        value: PALETTE.bg,
    },
    { layerPattern: /^landuse_residential$/, property: 'fill-opacity', value: 0 },

    { layerPattern: /^water$/, property: 'fill-color', value: PALETTE.water },
    {
        layerPattern: /^waterway$/,
        property: 'line-color',
        value: PALETTE.waterway,
    },
    { layerPattern: /^waterway$/, property: 'line-opacity', value: 1 },

    {
        layerPattern: /^highway_(path|minor)$/,
        property: 'line-color',
        value: PALETTE.road,
    },
    {
        layerPattern: /^highway_(major_inner|motorway_inner|motorway_bridge_inner)$/,
        property: 'line-color',
        value: PALETTE.roadMajor,
    },
    {
        layerPattern: /casing$/,
        property: 'line-color',
        value: PALETTE.roadCasing,
    },
    { layerPattern: /casing$/, property: 'line-opacity', value: 0 },
    {
        layerPattern: /^highway_(major_subtle|motorway_subtle)$/,
        property: 'line-opacity',
        value: 0,
    },
    {
        layerPattern: /^tunnel_motorway_inner$/,
        property: 'line-color',
        value: PALETTE.road,
    },
    {
        layerPattern: /^tunnel_motorway_inner$/,
        property: 'line-opacity',
        value: 0.4,
    },
    {
        layerPattern: /^tunnel_motorway_inner$/,
        property: 'line-width',
        value: thin([8, 0.4, 12, 0.8, 16, 1.6, 18, 3]),
    },
    {
        layerPattern: /^highway_path$/,
        property: 'line-opacity',
        value: thin([15, 0.1, 17, 0.4, 18, 0.65]),
    },
    {
        layerPattern: /^highway_path$/,
        property: 'line-width',
        value: thin([15, 0.25, 17, 0.6, 18, 1.2]),
    },
    {
        layerPattern: /^highway_minor$/,
        property: 'line-opacity',
        value: thin([12.2, 0.4, 14, 0.8, 16, 0.95]),
    },
    {
        layerPattern: /^highway_minor$/,
        property: 'line-width',
        value: thin([12.5, 0.55, 15, 1.15, 17, 2.3]),
    },
    {
        layerPattern: /^highway_major_inner$/,
        property: 'line-opacity',
        value: thin([10, 0.75, 14, 0.95]),
    },
    {
        layerPattern: /^highway_major_inner$/,
        property: 'line-width',
        value: thin([10, 0.7, 13, 1.15, 16, 2.2, 18, 3.8]),
    },
    {
        layerPattern: /^highway_(motorway_inner|motorway_bridge_inner)$/,
        property: 'line-opacity',
        value: thin([8, 0.45, 13, 0.7, 16, 0.9]),
    },
    {
        layerPattern: /^highway_(motorway_inner|motorway_bridge_inner)$/,
        property: 'line-width',
        value: thin([8, 0.55, 12, 1.05, 15, 1.8, 18, 4.2]),
    },

    { layerPattern: /^railway/, property: 'line-opacity', value: 0 },
    { layerPattern: /^aeroway_/, property: 'line-opacity', value: 0 },

    {
        layerPattern: /^boundary_/,
        property: 'line-color',
        value: PALETTE.boundary,
    },
    { layerPattern: /^boundary_/, property: 'line-opacity', value: 0.35 },

    {
        layerPattern: /^place_(city_large|capital_gen0|capital_gen1|country_major)$/,
        property: 'text-color',
        value: PALETTE.labelMajor,
    },
    {
        layerPattern: /^place_(city|town|state|country_other|continent)$/,
        property: 'text-color',
        value: PALETTE.label,
    },
    { layerPattern: /^place_/, property: 'text-color', value: PALETTE.label },
    {
        layerPattern: /^place_/,
        property: 'text-halo-color',
        value: PALETTE.labelHalo,
    },
    { layerPattern: /^place_/, property: 'text-halo-width', value: 1.1 },
    { layerPattern: /^place_/, property: 'text-halo-blur', value: 0.4 },
    {
        layerPattern: /^place_(village|suburb|other)$/,
        property: 'text-opacity',
        value: 0.8,
    },

    {
        layerPattern: /^highway_name_/,
        property: 'text-color',
        value: PALETTE.label,
    },
    {
        layerPattern: /^highway_name_/,
        property: 'text-halo-color',
        value: PALETTE.labelHalo,
    },
    { layerPattern: /^highway_name_/, property: 'text-halo-width', value: 1.1 },
    {
        layerPattern: /^highway_name_/,
        property: 'text-opacity',
        value: 1,
    },

    {
        layerPattern: /^highway_shield_/,
        property: 'text-color',
        value: PALETTE.shieldText,
    },
    {
        layerPattern: /^highway_shield_/,
        property: 'text-halo-color',
        value: PALETTE.labelHalo,
    },
    { layerPattern: /^highway_shield_/, property: 'icon-opacity', value: 0 },
    { layerPattern: /^highway_shield_/, property: 'text-opacity', value: 0 },

    {
        layerPattern: /^water_name_/,
        property: 'text-color',
        value: PALETTE.waterLabel,
    },
    {
        layerPattern: /^water_name_/,
        property: 'text-halo-color',
        value: PALETTE.labelHalo,
    },
    { layerPattern: /^water_name_/, property: 'text-halo-width', value: 1.1 },
    { layerPattern: /^water_name_/, property: 'text-opacity', value: 0.7 },

    { layerPattern: /^poi_/, property: 'text-opacity', value: 0 },
    { layerPattern: /^poi_/, property: 'icon-opacity', value: 0 },

    { layerPattern: /^airport_label_/, property: 'text-opacity', value: 0.3 },
];

type MapStyleLayer = {
    id?: string;
    type?: string;
    minzoom?: number;
    paint?: Record<string, unknown>;
    layout?: Record<string, unknown>;
};

type MapStyleJson = {
    layers?: MapStyleLayer[];
    [key: string]: unknown;
};

export type MapLabelLanguage = 'ru' | 'en';

const cachedByLanguage = new Map<MapLabelLanguage, MapStyleJson>();
let rawStyle: MapStyleJson | null = null;
let rawPromise: Promise<MapStyleJson | null> | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

export const normalizeMapLanguage = (language?: string | null): MapLabelLanguage =>
    (language || '').toLowerCase().startsWith('ru') ? 'ru' : 'en';

const canApplyPaint = (layerType: string | undefined, property: PaintProperty): boolean => {
    if (!layerType) return false;
    if (property === 'background-color') return layerType === 'background';
    if (property === 'fill-color' || property === 'fill-opacity') return layerType === 'fill';
    if (property === 'line-color' || property === 'line-opacity' || property === 'line-width') {
        return layerType === 'line';
    }
    if (property.startsWith('text-') || property === 'icon-opacity') return layerType === 'symbol';

    return false;
};

/** Слои, которые убираем целиком. Здания нужны для адресных подписей. */
const shouldDropLayer = (id: string): boolean =>
    /^poi_/i.test(id) || /^railway/i.test(id) || /^aeroway_/i.test(id);

/** Одно имя: кириллица для ru, латиница для остальных, без второй строки. */
const singleNameField = (language: MapLabelLanguage): unknown[] => {
    const preferNonLatin = language === 'ru';
    const chosen = preferNonLatin
        ? ['case', ['!=', ['var', 'nonlatin'], ''], ['var', 'nonlatin'], ['var', 'latin']]
        : ['case', ['!=', ['var', 'latin'], ''], ['var', 'latin'], ['var', 'nonlatin']];

    return [
        'let',
        'latin',
        ['coalesce', ['get', 'name:latin'], ''],
        'nonlatin',
        [
            'case',
            ['all', ['has', 'name:nonlatin'], ['is-supported-script', ['get', 'name:nonlatin']]],
            ['get', 'name:nonlatin'],
            '',
        ],
        chosen,
    ];
};

const usesBilingualName = (field: unknown): boolean => {
    const serialized = JSON.stringify(field);

    return serialized.includes('name:latin') || serialized.includes('name:nonlatin');
};

/** Палитра + одна строка подписи + здания и адреса для точного редактирования. */
const patchRefStyle = (style: MapStyleJson, language: MapLabelLanguage): MapStyleJson => {
    const nameField = singleNameField(language);
    const layers = (style.layers || [])
        .filter((layer) => !layer.id || !shouldDropLayer(layer.id))
        .map((layer) => {
            const id = layer.id;
            const paint = layer.paint ? { ...layer.paint } : {};
            let paintChanged = false;
            let layout = layer.layout;
            let minzoom = layer.minzoom;

            if (id) {
                for (const { layerPattern, property, value } of PAINT_OVERRIDES) {
                    if (!layerPattern.test(id)) continue;
                    if (!canApplyPaint(layer.type, property)) continue;

                    paint[property] = value;
                    paintChanged = true;
                }
            }

            if (id === 'highway_major_inner' && (minzoom == null || minzoom > 10)) {
                minzoom = 10;
            }

            const textField = layout?.['text-field'];

            if (textField != null && usesBilingualName(textField)) {
                layout = { ...layout, 'text-field': nameField };
            }

            if (!paintChanged && layout === layer.layout && minzoom === layer.minzoom) return layer;

            return {
                ...layer,
                ...(minzoom !== layer.minzoom ? { minzoom } : {}),
                ...(paintChanged ? { paint } : {}),
                ...(layout !== layer.layout ? { layout } : {}),
            };
        });

    layers.push({
        id: 'admin-house-numbers',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'housenumber',
        minzoom: 17,
        layout: {
            'text-field': ['get', 'housenumber'],
            'text-font': ['Stadia Regular'],
            'text-size': 12,
            'text-padding': 2,
        },
        paint: {
            'text-color': '#374151',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
        },
    } as MapStyleLayer);
    return { ...style, layers };
};

const loadRawStyle = async (): Promise<MapStyleJson | null> => {
    if (rawStyle) return rawStyle;

    if (!rawPromise) {
        rawPromise = (async () => {
            try {
                const response = await fetch(STADIA_DARK_STYLE_URL);

                if (!response.ok) {
                    throw new Error(`Map style HTTP ${response.status}`);
                }

                const json: unknown = await response.json();

                if (!isRecord(json) || !Array.isArray(json.layers)) {
                    throw new Error('Invalid map style JSON');
                }

                rawStyle = json as MapStyleJson;

                return rawStyle;
            } catch {
                rawPromise = null;

                return null;
            }
        })();
    }

    return rawPromise;
};

/** Синхронный кэш — чтобы MapView не монтировался дважды (null → style). */
export const getCachedRoutellersMapStyle = (language?: string | null): MapStyleJson | null =>
    cachedByLanguage.get(normalizeMapLanguage(language)) ?? null;

/** Стиль карты. При ошибке сети — сырой URL Stadia. */
export const getRoutellersMapStyle = async (
    language?: string | null,
): Promise<MapStyleJson | string> => {
    const lang = normalizeMapLanguage(language);
    const cached = cachedByLanguage.get(lang);

    if (cached) return cached;

    const raw = await loadRawStyle();

    if (!raw) return STADIA_DARK_STYLE_URL;

    const patched = patchRefStyle(raw, lang);

    cachedByLanguage.set(lang, patched);

    return patched;
};

/** Синхронный URL до загрузки патча. */
export const getVectorStyleUrl = (): string => STADIA_DARK_STYLE_URL;
